// Menu and ContextMenu: commands in the kit overlay. Menu hangs from a trigger (a page header's
// Tools, a member's role); ContextMenu opens at the point that was right-clicked. Both carry the
// full menu keyboard (arrows wrap, Home and End, typeahead, Escape hands focus back, choosing
// closes) and one row vocabulary: a hint says what a command acts on, danger reads red, a choice
// or a switch carries the gold check, and a destructive command can ask in place.
// docs/operations/design-standard.md, "Controls".

import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from './Button';
import { FadeIn } from './FadeIn';
import { IconCheck, IconChevronDown } from './icons';
import { MENU_HEADING, MENU_RULE, MenuPanel, followPointer, menuRow, printable, rowsIn, stepRows, useTypeahead } from './MenuPanel';

export interface MenuItem {
  /** Stable identity when labels can repeat or change. Defaults to the label. */
  key?: string;
  label: string;
  /** One line under the label: what the command acts on ("128 rows, current filters"). */
  hint?: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Removes or undoes something: red words. Give these the last group. */
  tone?: 'danger';
  /** A kit icon before the label. */
  icon?: React.ReactNode;
  /** A short note at the row's end: a state word, an owner, a shortcut. */
  meta?: React.ReactNode;
  /** `radio`: one choice in its group, and choosing closes. `checkbox`: a switch that flips in place. */
  kind?: 'radio' | 'checkbox';
  checked?: boolean;
  /** Ask first: the row becomes the question, with `yes` and `no` (default "Keep") beside it. */
  confirm?: { question: string; yes: string; no?: string };
}

/** Items under an optional name. A group with no name is set off by a rule. */
export interface MenuGroup {
  group?: string;
  items: MenuItem[];
}

export type MenuEntry = MenuItem | MenuGroup;

const isGroup = (e: MenuEntry): e is MenuGroup => 'items' in e;
const keyOf = (it: MenuItem) => it.key ?? it.label;

const groupsOf = (entries: MenuEntry[]): MenuGroup[] => {
  const out: MenuGroup[] = [];
  let loose: MenuItem[] | null = null;
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i];
    if (isGroup(e)) {
      loose = null;
      out.push(e);
    } else {
      if (!loose) {
        loose = [];
        out.push({ items: loose });
      }
      loose.push(e);
    }
  }
  return out.filter((g) => g.items.length > 0);
};

const WIDTH = 'min-w-[12rem] max-w-[min(22rem,calc(100vw-16px))]';
const TITLE = 'truncate px-2 pb-1 pt-1.5 text-[13px] font-semibold text-on-surface';
const NOTE = 'px-2 pb-1.5 pt-1 text-xs text-on-surface-variant';

interface BodyOptions {
  entries: MenuEntry[];
  open: boolean;
  close: (refocus: boolean) => void;
  onTab: (e: React.KeyboardEvent) => void;
  panel: React.RefObject<HTMLDivElement>;
}

/** The rows, their keyboard and their in-place question, shared by Menu and ContextMenu. */
function useMenuBody({ entries, open, close, onTab, panel }: BodyOptions) {
  const [asking, setAsking] = useState<string | null>(null);
  const [flipped, setFlipped] = useState<string | null>(null);
  const returnTo = useRef<string | null>(null);
  const typeahead = useTypeahead();

  useEffect(() => {
    if (open) return;
    setAsking(null);
    setFlipped(null);
    returnTo.current = null;
  }, [open]);

  // Focus follows the question in (to the safe answer) and back out (to the row that asked).
  useEffect(() => {
    const root = panel.current;
    if (!open || !root) return;
    if (asking) {
      root.querySelector<HTMLElement>('[data-keep]')?.focus();
    } else if (returnTo.current) {
      const back = returnTo.current;
      returnTo.current = null;
      rowsIn(root).find((r) => r.dataset.key === back)?.focus();
    }
  }, [asking]); // eslint-disable-line react-hooks/exhaustive-deps

  const unask = () => {
    returnTo.current = asking;
    setAsking(null);
  };

  const run = (it: MenuItem) => {
    if (it.disabled) return;
    const k = keyOf(it);
    if (it.confirm) {
      setAsking(k);
      return;
    }
    setAsking(null);
    if (it.kind === 'checkbox') {
      setFlipped(k);
      it.onSelect();
      return;
    }
    // Closed first: a command that opens a dialog or moves focus then takes it from the trigger.
    close(true);
    it.onSelect();
  };

  const onOpened = () => {
    const root = panel.current;
    const chosen = root?.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]:not(:disabled)');
    (chosen || rowsIn(root)[0])?.focus();
  };

  // Keys the menu uses stop here, so a page's own shortcuts (a table's J, K and Enter) never act
  // on them as well. Enter and Space still reach the focused row, which is a button.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const rows = rowsIn(panel.current);
    if (stepRows(e, rows)) {
      e.stopPropagation();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (asking) unask(); else close(true);
    } else if (e.key === 'Tab') {
      onTab(e);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    } else if (printable(e)) {
      e.preventDefault();
      e.stopPropagation();
      typeahead(e.key, rows)?.focus();
    }
  };

  const row = (it: MenuItem) => {
    const k = keyOf(it);
    const danger = it.tone === 'danger';
    if (it.confirm && asking === k) {
      const { question, yes, no = 'Keep' } = it.confirm;
      return (
        <div key={k} role="group" aria-label={question}>
          <FadeIn className="flex min-h-8 items-center gap-1 py-0.5 pl-2 pr-0.5 [@media(pointer:coarse)]:min-h-11">
            <span className="min-w-0 flex-1 truncate text-sm text-on-surface">{question}</span>
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              data-row=""
              data-label={yes}
              onClick={() => {
                setAsking(null);
                close(true);
                it.onSelect();
              }}
              className={buttonClass(danger ? 'danger' : 'tonal', 'sm')}
            >
              {yes}
            </button>
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              data-row=""
              data-keep=""
              data-label={no}
              onClick={unask}
              className={buttonClass('quiet', 'sm')}
            >
              {no}
            </button>
          </FadeIn>
        </div>
      );
    }
    const role = it.kind === 'radio' ? 'menuitemradio' : it.kind === 'checkbox' ? 'menuitemcheckbox' : 'menuitem';
    return (
      <button
        key={k}
        type="button"
        role={role}
        aria-checked={it.kind ? !!it.checked : undefined}
        tabIndex={-1}
        disabled={it.disabled}
        data-row=""
        data-key={k}
        data-label={it.label}
        onMouseMove={followPointer}
        onClick={() => run(it)}
        className={menuRow(danger ? 'danger' : 'plain')}
      >
        {it.icon && (
          <span aria-hidden="true" className={`flex h-4 w-4 flex-none items-center justify-center ${danger ? '' : 'text-on-surface-variant'}`}>
            {it.icon}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{it.label}</span>
          {it.hint && <span className="block text-xs text-on-surface-variant tabular-nums">{it.hint}</span>}
        </span>
        {it.meta && <span className="flex-none text-xs text-on-surface-variant tabular-nums">{it.meta}</span>}
        {it.kind && (
          <span
            className={`flex h-4 w-4 flex-none items-center justify-center text-primary transition-opacity duration-quick ease-emphasized ${
              it.checked ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* A switch flipped on draws its check; one already on when the menu opens just shows it. */}
            <IconCheck className="h-4 w-4" draw={it.kind === 'checkbox' && flipped === k && !!it.checked} />
          </span>
        )}
      </button>
    );
  };

  const content = groupsOf(entries).map((g, gi) => (g.group ? (
    <div key={`group-${gi}`} role="group" aria-label={g.group} className={gi > 0 ? 'mt-1' : ''}>
      <div aria-hidden="true" className={MENU_HEADING}>{g.group}</div>
      {g.items.map(row)}
    </div>
  ) : (
    <React.Fragment key={`group-${gi}`}>
      {gi > 0 && <div role="separator" className={MENU_RULE} />}
      {g.items.map(row)}
    </React.Fragment>
  )));

  return { content, onKeyDown, onOpened, layoutKey: asking };
}

let seq = 0;
const useIds = () => useState(() => {
  seq += 1;
  return { panel: `ui-menu-${seq}`, note: `ui-menu-${seq}-note` };
})[0];

/** Everything a custom trigger spreads onto its button to open the menu. */
export interface MenuTriggerProps {
  ref: React.RefObject<HTMLButtonElement>;
  type: 'button';
  disabled?: boolean;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls'?: string;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}

export interface MenuProps {
  /** The trigger's text and the menu's accessible name. */
  label: string;
  items: MenuEntry[];
  /** The trigger key: `quiet` inside a panel or a row, `tonal` on the page background. */
  variant?: 'quiet' | 'tonal';
  size?: 'sm' | 'md';
  /** The trigger edge the menu lines up with. It swaps when that side has no room. */
  align?: 'start' | 'end';
  /** One line above the items: what choosing will cost, or why the list is short. */
  note?: string;
  disabled?: boolean;
  /** Draws a different trigger (an icon key, a key showing the current value) from these props. */
  trigger?: (props: MenuTriggerProps, open: boolean) => React.ReactElement;
  className?: string;
}

export const Menu: React.FC<MenuProps> = ({
  label,
  items,
  variant = 'quiet',
  size = 'md',
  align = 'end',
  note,
  disabled,
  trigger,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const ids = useIds();

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const body = useMenuBody({
    entries: items,
    open,
    close,
    panel,
    // Focus back to the trigger before the panel goes, so the browser's Tab moves on from there.
    onTab: () => {
      triggerRef.current?.focus();
      setOpen(false);
    },
  });

  const props: MenuTriggerProps = {
    ref: triggerRef,
    type: 'button',
    disabled,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? ids.panel : undefined,
    onClick: () => (open ? close(true) : setOpen(true)),
    onKeyDown: (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        // With every item disabled nothing takes focus on open, so Escape works from here too.
        e.preventDefault();
        e.stopPropagation();
        close(true);
      }
    },
  };

  return (
    <>
      {trigger ? trigger(props, open) : (
        <button
          {...props}
          className={`${buttonClass(variant, size)} aria-expanded:bg-surface-container-high aria-expanded:text-on-surface ${className}`}
        >
          {label}
          <IconChevronDown
            className={`h-4 w-4 transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}
      <MenuPanel
        ref={panel}
        open={open}
        anchor={triggerRef}
        onDismiss={close}
        role="menu"
        label={label}
        id={ids.panel}
        aria-describedby={note ? ids.note : undefined}
        align={align}
        layoutKey={body.layoutKey}
        onOpened={body.onOpened}
        onKeyDown={body.onKeyDown}
        className={WIDTH}
      >
        {note && <p id={ids.note} className={NOTE}>{note}</p>}
        {body.content}
      </MenuPanel>
    </>
  );
};

export interface ContextMenuProps {
  /** Where the press landed, in client coordinates. Null keeps the menu closed. */
  at: { x: number; y: number } | null;
  /** The menu's accessible name: "Actions for Summer show". */
  label: string;
  /** The thing the commands act on, as the menu's first line. */
  title?: string;
  /** One line above the items. */
  note?: string;
  items: MenuEntry[];
  /** It closed. `refocus` is true when it closed from the keyboard or a command ran. */
  onClose: (refocus: boolean) => void;
  /** Where focus goes back to when `refocus` is true. */
  returnFocus?: HTMLElement | null;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ at, label, title, note, items, onClose, returnFocus }) => {
  const panel = useRef<HTMLDivElement>(null);
  // Held past closing, so the menu leaves from where it was.
  const last = useRef(at ?? { x: 0, y: 0 });
  if (at) last.current = at;
  const open = at !== null;
  const ids = useIds();

  const close = (refocus: boolean) => {
    if (refocus && returnFocus?.isConnected) returnFocus.focus();
    onClose(refocus);
  };

  const body = useMenuBody({
    entries: items,
    open,
    close,
    panel,
    onTab: (e) => {
      e.preventDefault();
      close(true);
    },
  });

  return (
    <MenuPanel
      ref={panel}
      open={open}
      anchor={last.current}
      onDismiss={close}
      role="menu"
      label={label}
      id={ids.panel}
      aria-describedby={note ? ids.note : undefined}
      layoutKey={body.layoutKey}
      onOpened={body.onOpened}
      onKeyDown={body.onKeyDown}
      className={WIDTH}
    >
      {title && <p aria-hidden="true" className={TITLE}>{title}</p>}
      {note && <p id={ids.note} className={NOTE}>{note}</p>}
      {body.content}
    </MenuPanel>
  );
};

export default Menu;
